import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  Minus,
  ArrowRight,
  Users,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import DcPage from "@/components/landing/dcv2/DcPage";
import SegmentedControl from "@/components/landing/dcv2/SegmentedControl";
import { pricingApi } from "@/services/endpoints/pricing";
import type { PublicPlan } from "@/services/api/types";
import featureLabels from "@/data/plan-features.json";
import { BASE_DISPLAY_CURRENCY } from "@/utils/display-currency";

/**
 * Pricing, on the v2 canvas.
 *
 * The page wraps in <DcPage>, which supplies the nav, the footer, the two
 * families, the eleven custom properties, the theme toggle and the scroll
 * runtime, and every section below is authored in that language: inline style
 * objects rather than Tailwind, `var(--paper2)` / `rgba(var(--ink-rgb),…)`
 * rather than the `--landing-*` tokens, and Manrope throughout. Eyebrows,
 * labels, numerals and metadata used to run in JetBrains Mono; the site
 * consolidated to a single family on 2026-09-18, so those keep their tracking
 * and case but not their own face. Numerals that share a column carry
 * `font-variant-numeric: tabular-nums` to stay aligned without one.
 *
 * Nothing about the catalog changed. Every price, plan name, feature label,
 * limit, FAQ answer and destination is the copy this page already shipped; the
 * geo-resolved display currency, the monthly/annual toggle, the middle-tier
 * recommendation rule and the /contact checkout route are untouched.
 *
 * Consequences of the restyle worth naming:
 *
 * - Motion. The framer-motion work — the shared scroll timeline that sliced the
 *   plan cards in, the per-row table reveals, the `layoutId` pill that slid
 *   between billing options, the height-animated FAQ panels — is replaced by
 *   the canvas's one vocabulary: `data-rv` fades a block up with an 80ms
 *   sibling stagger, `data-plx` parallaxes a decorative bloom. There are no
 *   observers or scroll listeners left on this route, and `prefers-reduced-
 *   motion` is handled once in the runtime rather than branched on here.
 *   `data-rv` is deliberately absent from anything downstream of the billing
 *   toggle or the fetch: the reveal observer runs once at mount, so a block
 *   that re-renders could otherwise be left at opacity 0.
 * - The one saturated colour. It used to be a literal `var(--acc2)`; it is now
 *   `var(--acc2)`, so it follows the theme instead of fighting the light one.
 * - The recommended tier. It used to be a cyan border and a cyan glow. It is
 *   now the canvas's inverted card — `var(--chip)` on `var(--chipfg)` — which
 *   is how the homepage's capability grid marks its one different card, and it
 *   reads as the recommendation at a glance in both themes.
 * - AmbientField and Spotlight belonged to the previous shell's gradient
 *   surface. The canvas's equivalent is the parallaxed cyan bloom.
 * - The closing FAQ band is dark (`#101216`), so the page lands on the same
 *   ground its footer stands on, the way every other converted route does.
 */

/* ─────────────────────── Canvas tokens ─────────────────────── */

const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";
const RULE = "1px solid rgba(var(--ink-rgb),.2)";

/* The dark band's own three values, literal because the band does not theme. */
const DARK_GROUND = "#101216";
const DARK_INK = "#F2F4F6";
const DARK_ACCENT = "#38E5FF";
const DARK_DIM = "rgba(242,244,246,.68)";
const DARK_RULE = "1px solid rgba(242,244,246,.2)";

/* Vertical padding only. The side gutter is `.site-gutter` on each section, so
   it can narrow on a phone (global.css, T101026002). */
const SECTION: React.CSSProperties = {
  paddingTop: 118,
  paddingBottom: 118,
  position: "relative",
  overflow: "clip",
};

const INNER: React.CSSProperties = {
  maxWidth: 1240,
  margin: "0 auto",
  position: "relative",
};

const H2: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: "clamp(28px, 3.4vw, 46px)",
  letterSpacing: "-0.02em",
  lineHeight: 1.14,
};

const H3: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 18,
  letterSpacing: "-0.01em",
};

const BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  color: "rgba(var(--ink-rgb),.66)",
};

const CARD_BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  color: "rgba(var(--ink-rgb),.66)",
};

const BTN_PRIMARY: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  background: "var(--btn)",
  color: "var(--btnfg)",
  padding: "15px 28px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  border: "none",
  cursor: "pointer",
  fontFamily: SANS,
  transition: "transform .2s, box-shadow .2s",
};

const BTN_GHOST: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid rgba(var(--ink-rgb),.3)",
  color: "var(--ink)",
  padding: "15px 28px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  background: "transparent",
  cursor: "pointer",
  fontFamily: SANS,
  transition: "border-color .2s, background .2s",
};

/** Visually hidden, still announced. Replaces Tailwind's `sr-only`. */
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0,
};

/**
 * The eyebrow slot, carrying the canvas's mark and nothing else. The homepage
 * pairs the glyph with a label; this page has no label to pair it with and
 * inventing one would be new copy, so the mark stands alone as the rhythm mark
 * above each heading.
 */
function Mark({ dark = false }: { dark?: boolean }) {
  return (
    <div
      aria-hidden="true"
      style={{
        fontFamily: MONO,
        fontSize: 11.5,
        letterSpacing: ".16em",
        textTransform: "uppercase",
        color: dark ? DARK_ACCENT : "var(--link)",
        marginBottom: 18,
      }}
    >
      <span className="dc-mark" aria-hidden="true" />
    </div>
  );
}

/** A decorative cyan bloom, parallaxed by the shared runtime. */
/**
 * The recommended tier inverts to the chip colour, exactly the way the
 * homepage's fourth capability card does. Text inside it is tinted off
 * `--chipfg` rather than off `--ink`, which is what these two helpers are for —
 * a card that inverts cannot reuse the page's ink alphas.
 */
function dimOn(inverted: boolean): string {
  return inverted
    ? "color-mix(in srgb, var(--chipfg) 66%, transparent)"
    : "rgba(var(--ink-rgb),.66)";
}

function hairlineOn(inverted: boolean): string {
  return inverted
    ? "1px solid color-mix(in srgb, var(--chipfg) 20%, transparent)"
    : HAIRLINE;
}

/**
 * Narrow-viewport flag, for the places an inline style cannot express a media
 * query. Mirrors the matchMedia use already in LandingV5 and Solutions rather
 * than introducing a stylesheet for one rule.
 *
 * Starts false so the server-rendered/first paint matches the desktop default,
 * then corrects on mount; the listener keeps it honest through rotation and
 * window resize.
 */
function useIsNarrow(maxWidthPx = 640): boolean {
  const [narrow, setNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidthPx}px)`);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [maxWidthPx]);

  return narrow;
}

type BillingInterval = "monthly" | "yearly";

/**
 * Period shown beside "Free" on the trial card, in the slot a paid plan uses
 * for "CAD/mo".
 *
 * CAVEAT — this is the ONLY hardcoded figure on this page. The catalog carries
 * no trial-length column, so the length cannot be read from /public/pricing the
 * way every price is. The trial plan's `description` (editable in Admin Console)
 * states the same duration independently, so changing the trial length means
 * editing BOTH: the description there and this constant here, in a deploy.
 */
const TRIAL_PERIOD_LABEL = "for 14 days";

/**
 * Feature bullets shown on a card before the rest collapse behind a toggle.
 * The full matrix is the comparison table further down the page, so a card only
 * has to carry enough to be scannable against its neighbours.
 */
const FEATURES_BEFORE_FOLD = 5;

/**
 * Checks shown on an add-on tile. Lower than a plan's fold because an add-on
 * tile is a quarter of the width and there are eighteen of them: the catalog
 * ran to half the page when these carried full lists. Modules ship 1–12
 * features, so four covers most of them outright and the rest say how many
 * more there are.
 */
const ADDON_CHECKS = 4;

/**
 * The card owns the list; the grid's shared toggle owns `aria-controls` for
 * every one of them. Both derive the id here so they cannot drift apart and
 * silently break the association.
 */
function planFeatureListId(planId: string): string {
  return `plan-${planId}-features`;
}

/** Compact human form for large token allowances (1_000_000 -> "1M"). */
function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return String(n);
}

/**
 * Display copy for the feature ids the plans catalog ships, keyed by id.
 * Reword a pricing bullet by editing the JSON — no component change needed.
 */
const FEATURE_LABELS: Record<string, string> = featureLabels;

/** Words that stay fully capitalised when an unmapped key is humanised. */
const FEATURE_ACRONYMS = new Set([
  "api",
  "sso",
  "hr",
  "ai",
  "crm",
  "sla",
  "pto",
  "pdf",
  "csv",
  "2fa",
  "mfa",
]);

/** Kept lowercase mid-title ("proof_of_delivery" -> "Proof of Delivery"). */
const FEATURE_MINOR_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "in",
  "nor",
  "of",
  "on",
  "or",
  "per",
  "the",
  "to",
  "via",
  "vs",
  "with",
]);

/** Matches the snake_case ids the plans endpoint ships ("employee_management"). */
const FEATURE_KEY = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;

/**
 * Feature entries arrive as backend identifiers, not display copy
 * ("api_access" -> "API Access", "extra_50gb_storage" -> "Extra 50GB Storage").
 * Curated copy wins; a key the catalog adds before the JSON catches up is
 * title-cased on the fly so it never renders with underscores. Values that
 * aren't snake_case ids are already readable and pass through untouched.
 */
function formatFeature(value: string): string {
  const label = FEATURE_LABELS[value];
  if (label) return label;

  if (!FEATURE_KEY.test(value)) return value;

  const words = value.split("_");
  return words
    .map((word, i) => {
      if (FEATURE_ACRONYMS.has(word)) return word.toUpperCase();
      // Storage units come glued to their number ("50gb" -> "50GB").
      const sized = /^(\d+)(gb|tb|mb)$/.exec(word);
      if (sized) return `${sized[1]}${sized[2].toUpperCase()}`;
      // Minor words stay lowercase unless they open or close the label.
      const isEdge = i === 0 || i === words.length - 1;
      if (!isEdge && FEATURE_MINOR_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Money in the currency it is actually denominated in.
 *
 * This page used to concatenate a hardcoded "$" with whatever currency code the
 * plan resolved to, so a plan sold in EUR rendered "$1,200 EUR/mo". The symbol
 * now comes from the currency itself. `narrowSymbol` keeps the figure short
 * ("$1,200" rather than "CA$1,200") and the ISO code is printed beside it, so
 * two currencies that share a glyph are still told apart. Both the option and an
 * unrecognised code can throw, hence the two-step fallback — a price is the last
 * thing on this page that may fail to render.
 */
function formatMoney(amount: number, currency: string): string {
  const code = currency.toUpperCase();
  // Catalog amounts are major units and usually whole; only show cents if there
  // are any, so "$1,200" never becomes "$1,200.00".
  const digits = Number.isInteger(amount) ? 0 : 2;
  const base = {
    style: "currency" as const,
    currency: code,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  };
  try {
    return new Intl.NumberFormat(undefined, { ...base, currencyDisplay: "narrowSymbol" }).format(amount);
  } catch {
    try {
      return new Intl.NumberFormat(undefined, base).format(amount);
    } catch {
      return `${code} ${amount.toLocaleString()}`;
    }
  }
}

type ResolvedPrice = {
  /** The currency the figures below are really denominated in. */
  currency: string;
  amount: number;
  hasPrice: boolean;
  /**
   * Costs nothing, as opposed to merely having no published price.
   *
   * `hasPrice: false` alone cannot tell those apart — the free trial and an
   * unpriced "contact us" tier are both amount 0, and rendering the trial as
   * "Custom" (the old behaviour) told visitors to enquire about a plan they can
   * just start. The backend settles it with `isTrial`, derived from the plan's
   * catalog alias; the two flags are deliberately independent.
   */
  isFree: boolean;
  /** Difference against twelve monthly payments, when the annual price is lower. */
  savings: number;
  /** Per-employee-seat amount, when the plan has one. */
  seatPrice: number;
};

/**
 * Real price points only, never FX math: a plan not sold in the selected
 * currency shows its base-currency price, labeled with the actual currency.
 *
 * Extracted so the cards and the comparison table cannot drift apart — there is
 * one place where a displayed price is decided, and it never computes a rate.
 */
function resolvePrice(
  plan: PublicPlan,
  currency: string,
  billingInterval: BillingInterval
): ResolvedPrice {
  const point = plan.prices?.[currency];
  const resolvedCurrency = point ? currency : (plan.currency ?? BASE_DISPLAY_CURRENCY);
  const monthly = point?.monthly ?? plan.priceMonthly;
  const yearly = point?.yearly ?? plan.priceYearly;
  const amount = billingInterval === "yearly" ? yearly : monthly;
  const savings =
    billingInterval === "yearly" && yearly > 0 && yearly < monthly * 12
      ? monthly * 12 - yearly
      : 0;
  // Per-seat price (core repackage): the amount above is the flat base fee that
  // covers the owner; this is billed per purchased employee seat. Same display
  // currency as the flat price. Absent -> flat-only plan.
  const seatPrice =
    (billingInterval === "yearly" ? point?.seatYearly : point?.seatMonthly) ?? 0;

  return {
    currency: resolvedCurrency,
    amount,
    hasPrice: amount > 0,
    // Trust the catalog flag, not the zero: a plan is free because the catalog
    // says it is the trial edition, never because its amount happens to be 0.
    isFree: plan.isTrial === true && amount === 0,
    savings,
    seatPrice,
  };
}

/** Suffix for a price, e.g. "CAD/mo". */
function priceSuffix(currency: string, billingInterval: BillingInterval): string {
  return `${currency.toUpperCase()}${billingInterval === "yearly" ? "/yr" : "/mo"}`;
}

/**
 * The price line's two slots: a large figure and the small unit beside it.
 *
 * The trial reuses the SAME two slots as a paid plan ("Free" + "for 14 days"
 * where "$39" + "CAD/mo" would go) rather than putting the whole phrase in the
 * figure — at the 42px figure size "Free for 14 days" would wrap to two lines
 * and break the baseline the card row shares.
 *
 * A plan with no published price keeps a bare "Custom" and no unit, because
 * there is no period to quote until someone asks for a quote.
 */
function priceSlots(
  price: ResolvedPrice,
  billingInterval: BillingInterval
): { figure: string; unit: string | null } {
  if (price.isFree) {
    return { figure: "Free", unit: TRIAL_PERIOD_LABEL };
  }
  if (price.hasPrice) {
    return {
      figure: formatMoney(price.amount, price.currency),
      unit: priceSuffix(price.currency, billingInterval),
    };
  }
  return { figure: "Custom", unit: null };
}

function MetaBadge({
  icon: Icon,
  label,
  inverted = false,
}: {
  icon: typeof Users;
  label: string;
  inverted?: boolean;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        borderRadius: 999,
        border: hairlineOn(inverted),
        padding: "6px 13px",
        fontFamily: MONO,
        fontSize: 11.5,
        lineHeight: 1.3,
        color: dimOn(inverted),
      }}
    >
      <Icon size={12} aria-hidden="true" style={{ flex: "none" }} />
      {label}
    </span>
  );
}

/*
 * The billing-interval segment used to be defined here, and an identical one
 * was defined again in JobPostings for Newest/Oldest. Both are now the shared
 * `SegmentedControl` in components/landing/dcv2.
 *
 * The move is an accessibility fix, not a tidy-up. Both copies were buttons
 * carrying `aria-pressed`, which announces a toggle — "pressed" / "not
 * pressed" — and says nothing about the options being alternatives, how many
 * there are, or which is chosen. The shared control is a radiogroup, so it
 * announces "Monthly, radio button 1 of 2, selected", and it takes the
 * keyboard obligation that implies: one tab stop, arrows moving within.
 * Neither copy had arrow keys, so every segment was its own tab stop.
 */

function PlanCard({
  plan,
  billingInterval,
  currency,
  currencySwitchable,
  recommended,
  primaryCta,
  featuresExpanded,
  addons,
}: {
  plan: PublicPlan;
  billingInterval: BillingInterval;
  currency: string;
  /** Whether a currency switcher is on screen — decides if a fallback needs saying. */
  currencySwitchable: boolean;
  recommended: boolean;
  /** Carries the page's one filled button. Decided by PlanGrid; see `ctaPlanId`. */
  primaryCta: boolean;
  /**
   * Owned by PlanGrid, never by the card. Cards in this row exist to be
   * compared, so they have to fold and unfold together — see the note on the
   * grid's single control.
   */
  featuresExpanded: boolean;
  /**
   * The add-on catalog, so a tier can say what it does NOT include
   * (T091826006). Everything here arrives in the same /public/pricing payload
   * the card already renders from, so this costs no extra request — the page
   * simply stops treating tiers and modules as two unrelated sections.
   */
  addons: PublicPlan[];
}) {
  const price = resolvePrice(plan, currency, billingInterval);
  const slots = priceSlots(price, billingInterval);

  /* A plan priced in a currency the visitor did not pick needs to say so in
     words. Silent when there is no switcher, because then nothing was picked. */
  const showFallbackNote = currencySwitchable && price.currency !== currency;

  const headingId = `plan-${plan.id}-name`;

  /**
   * Storage and the AI allowance read as entitlements, not as badges
   * (T091826006). They used to sit in a badge row above the fold, where three
   * chips competed with the price for the first glance and said less than the
   * list below them did. They are list rows now, at the top because they are
   * the two numbers a buyer checks a plan against.
   *
   * Pre-formatted here rather than at render, so one array carries both kinds
   * of row and the fold count below counts what is actually on the card — the
   * old count was of `features` alone and would have under-reported by two.
   */
  const planRows = useMemo(() => {
    const rows: string[] = [];
    if (plan.maxStorageGb > 0) rows.push(`${plan.maxStorageGb} GB storage`);
    if (plan.includedTokensMonth > 0) {
      rows.push(`${formatTokens(plan.includedTokensMonth)} AI tokens per month`);
    }
    return [...rows, ...plan.features.map(formatFeature)];
  }, [plan.maxStorageGb, plan.includedTokensMonth, plan.features]);

  const foldedFeatureCount = planRows.length - FEATURES_BEFORE_FOLD;
  const visibleFeatures =
    featuresExpanded || foldedFeatureCount <= 0
      ? planRows
      : planRows.slice(0, FEATURES_BEFORE_FOLD);
  const featureListId = planFeatureListId(plan.id);

  /* A real cap is worth stating; the 999999 sentinel is not. The badge used to
     render "Unlimited users" from that sentinel, which contradicted the
     per-seat line above it on a seat-priced plan and overpromised on the
     contact tier. Removed per T091826006 — a plan with no meaningful cap simply
     says nothing about one. */
  const userCapLabel =
    price.seatPrice <= 0 && plan.maxUsers > 0 && plan.maxUsers < 999999
      ? `${plan.maxUsers.toLocaleString()} users`
      : null;

  /**
   * An unpriced tier is a conversation, not a checkout (T091826006).
   *
   * `hasPrice` is false and `isFree` is false only for a plan that has no
   * published price — the trial is excluded by `isFree`, because it costs
   * nothing rather than merely not saying. "Get Started" on a plan whose price
   * reads "Custom" promises a self-serve path that does not exist: the backend
   * refuses to check such a plan out, by design, because it has no Stripe
   * price.
   */
  const isContactTier = !price.hasPrice && !price.isFree;
  const ctaLabel = isContactTier ? "Contact sales" : "Get Started";

  return (
    /* The recommended tier is the canvas's inverted card, the same move the
       homepage's capability grid makes on its one different card.

       Rows are a SUBGRID of the plan grid (T091826006 retest: "the 2 buttons
       should align"). The card spans the grid's four row tracks — heading,
       price, CTA, body — so each part lines up with the same part of the card
       beside it. In normal flow the CTA sat wherever its own description and
       price block ended, and those are catalog text of any length: Enterprise's
       four-line description pushed its button below Core's. `rowGap: 0` keeps
       the grid's own gap out of the card's insides; browsers without subgrid
       fall back to the stacked card. The 13px top margin is the room the
       "Recommended" tab overhangs, kept on every card so tops stay level. */
    <article
      aria-labelledby={headingId}
      className={recommended ? "dc-h-card-dark" : "dc-h-card"}
      style={{
        position: "relative",
        marginTop: 13,
        display: "grid",
        gridRow: "span 4",
        gridTemplateRows: "subgrid",
        rowGap: 0,
        background: recommended ? "var(--chip)" : "var(--paper2)",
        color: recommended ? "var(--chipfg)" : undefined,
        border: recommended ? "1px solid var(--chip)" : HAIRLINE,
        borderRadius: 18,
        padding: 28,
        transition: recommended
          ? "transform .25s, box-shadow .25s"
          : "transform .25s, box-shadow .25s, border-color .25s",
      }}
    >
      {recommended && (
        <span
          style={{
            position: "absolute",
            /* Offsets from the padding box, inside the 1px border: the tab sits
               13px above the card's edge and 26px in from it. */
            left: 25,
            top: -14,
            zIndex: 2,
            borderRadius: 999,
            padding: "5px 14px",
            background: "var(--acc2)",
            color: "#101216",
            fontFamily: MONO,
            fontSize: 11,
            letterSpacing: ".1em",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          Recommended
        </span>
      )}

      {/* ── Row 1: heading ── */}
      <div>
        <h3 id={headingId} style={{ ...H3, fontSize: 20, fontWeight: 700 }}>
          {plan.name}
        </h3>
        {plan.description && (
          <p
            style={{
              ...CARD_BODY,
              marginTop: 8,
              lineHeight: 1.6,
              color: dimOn(recommended),
            }}
          >
            {plan.description}
          </p>
        )}
      </div>

      {/* ── Row 2: price ──
          The figure is Manrope 800 and the currency/period beside it is mono,
          so the number reads as a number and its unit reads as metadata. The
          notes under it (annual saving, seat price, currency fallback) belong
          to the same row, so a tier with none of them still starts its CTA on
          the same line as one with all three. */}
      <div style={{ marginTop: 22 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            columnGap: 9,
            rowGap: 4,
          }}
        >
          <span
            style={{
              fontFamily: SANS,
              fontWeight: 800,
              fontSize: "clamp(32px, 3.2vw, 42px)",
              lineHeight: 1.05,
              letterSpacing: "-0.025em",
            }}
          >
            {slots.figure}
          </span>
          {slots.unit !== null && (
            <span
              style={{
                fontFamily: MONO,
                fontSize: 12.5,
                color: dimOn(recommended),
              }}
            >
              {slots.unit}
            </span>
          )}
        </div>
        {price.savings > 0 && (
          <p
            style={{
              margin: "10px 0 0",
              fontFamily: MONO,
              fontSize: 11.5,
              lineHeight: 1.6,
              color: recommended ? DARK_ACCENT : "var(--link)",
            }}
          >
            {`${formatMoney(price.savings, price.currency)} less than twelve monthly payments`}
          </p>
        )}
        {price.seatPrice > 0 && (
          <p
            style={{
              margin: "8px 0 0",
              fontFamily: MONO,
              fontSize: 11.5,
              lineHeight: 1.6,
              color: dimOn(recommended),
            }}
          >
            {`plus ${formatMoney(price.seatPrice, price.currency)} ${priceSuffix(
              price.currency,
              billingInterval
            )} per employee seat`}
          </p>
        )}
        {showFallbackNote && (
          <p
            style={{
              margin: "8px 0 0",
              fontSize: 12.5,
              lineHeight: 1.6,
              color: dimOn(recommended),
            }}
          >
            {`Sold in ${price.currency.toUpperCase()} — we show the real price rather than a conversion.`}
          </p>
        )}
      </div>

      {/* ── Row 3: CTA ──
          Directly under the price (T091826006). It used to be pinned to the
          foot of the card, which on a phone put it a full screen below the
          number that made someone want it — a visitor who had decided had to
          scroll past the entire feature list to act on the decision.

          Its row is a shared subgrid track, so it lines up with the button on
          the card beside it however long each card's heading and price rows
          run. `alignSelf: start` keeps it at its own height inside that row. */}
      <Link
        to="/contact"
        aria-label={`${ctaLabel} with ${plan.name}`}
        className={primaryCta ? "dc-h-btn-cyan" : "dc-h-ghost"}
        style={{
          marginTop: 22,
          alignSelf: "start",
          display: "inline-flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          borderRadius: 999,
          padding: "14px 24px",
          fontWeight: 600,
          fontSize: 14.5,
          /* Must track `primaryCta`, not `recommended`, or the filled class
             above would sit on a transparent background — the two decide the
             same button and have to read the same flag. */
          ...(primaryCta
            ? {
                background: DARK_ACCENT,
                color: "#101216",
                border: `1px solid ${DARK_ACCENT}`,
                transition: "transform .2s, box-shadow .2s",
              }
            : {
                background: "transparent",
                color: "var(--ink)",
                border: "1px solid rgba(var(--ink-rgb),.3)",
                transition: "border-color .2s, background .2s",
              }),
        }}
      >
        {ctaLabel}
        <ArrowRight size={16} aria-hidden="true" />
      </Link>

      {/* ── Row 4: body ──
          Limits, features and modules. A flex column so the feature wrapper
          can take the slack when this card's body is shorter than the row. */}
      <div style={{ display: "flex", flexDirection: "column" }}>
        {/* ── Limits ──
            Only a real cap survives here; storage and the AI allowance are rows
            in the list below now. The wrapper renders at all only when there is
            something to put in it, so a plan with no stated cap does not leave
            an empty 20px gap under the button. */}
        {userCapLabel && (
          <div
            style={{
              marginTop: 18,
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <MetaBadge inverted={recommended} icon={Users} label={userCapLabel} />
          </div>
        )}

        {/* ── Features ──
            Ruled rows rather than a bulleted stack, so a long list stays
            scannable against the same list on the card beside it.

            The wrapper, not the list, absorbs the body row's slack, so the
            "+N more" line and the module list sit directly under the last
            visible feature instead of being pushed to the foot of the card. */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            margin: "24px 0 0",
          }}
        >
          <ul
            id={featureListId}
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
            }}
          >
            {visibleFeatures.map((feature, i) => (
              <li
                key={feature}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "11px 0",
                  borderTop: i === 0 ? undefined : hairlineOn(recommended),
                }}
              >
                <Check
                  size={14}
                  aria-hidden="true"
                  style={{
                    flex: "none",
                    marginTop: 3,
                    /* Card 04 of the homepage grid does the same: the inverted
                       ground takes the literal cyan, everything else the token. */
                    color: recommended ? DARK_ACCENT : "var(--acc2)",
                  }}
                />
                <span
                  style={{
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: dimOn(recommended),
                  }}
                >
                  {formatFeature(feature)}
                </span>
              </li>
            ))}
          </ul>
  
          {/* States how much is hidden, and is deliberately NOT a control — the
              row folds from one button under the grid, so a second affordance
              per card would imply the cards move independently, which is the
              behaviour this replaced. Plans carry different totals, so the
              count has to live here rather than on that shared button. */}
          {!featuresExpanded && foldedFeatureCount > 0 && (
            <p
              style={{
                margin: 0,
                padding: "12px 0 2px",
                fontFamily: MONO,
                fontSize: 12,
                letterSpacing: "0.02em",
                color: dimOn(recommended),
              }}
            >
              {`+${foldedFeatureCount} more`}
            </p>
          )}

          {/* ── Modules ──
              What the tier does not already contain (T091826006). The ticket
              asked the list to show the core modules; a list of what is IN a
              plan answers only half of "what do I get", because every module
              this platform sells is purchasable separately. Naming them here,
              with their price, turns the card from a feature list into the
              actual shape of the offer.

              The contact tier states inclusion instead of a shopping list: a
              plan with no published price has nothing to add a per-module
              charge to, and quoting add-on prices beside "Custom" would invite
              someone to do arithmetic on a number we have not given them. */}
          {isContactTier ? (
            <p
              style={{
                margin: "18px 0 0",
                paddingTop: 16,
                borderTop: hairlineOn(recommended),
                fontSize: 13,
                lineHeight: 1.6,
                color: dimOn(recommended),
              }}
            >
              Every module included, at whatever scale you need them.
            </p>
          ) : (
            addons.length > 0 && (
              <div
                style={{
                  marginTop: 18,
                  paddingTop: 16,
                  borderTop: hairlineOn(recommended),
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontFamily: MONO,
                    fontSize: 11,
                    letterSpacing: ".08em",
                    textTransform: "uppercase",
                    color: dimOn(recommended),
                  }}
                >
                  Add any module
                </p>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: 13,
                    lineHeight: 1.6,
                    color: dimOn(recommended),
                  }}
                >
                  {addons.map((addon) => addon.name).join(" · ")}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * The grid that holds the plan cards.
 *
 * Each PlanCard spans four of its row tracks as a subgrid (heading, price, CTA,
 * body), which is what lines the same part up across the cards in a row.
 * Anything else placed in this grid takes one row. A card must keep exactly
 * four in-flow children, one per track; its absolutely positioned
 * "Recommended" tab takes no track.
 */
const PLAN_GRID: React.CSSProperties = {
  display: "grid",
  gap: 18,
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 310px), 1fr))",
  alignItems: "stretch",
};

/**
 * A grid of plan cards.
 *
 * It used to own a shared scroll timeline that sliced the cards in one after
 * another. The cards re-render whenever the billing toggle flips, and the
 * canvas's reveal observer runs once at mount — so they carry no `data-rv` and
 * arrive with the section around them instead. That is a deliberate absence,
 * not an oversight: a re-rendered `data-rv` block can be left at opacity 0.
 */
function PlanGrid({
  plans,
  addons,
  billingInterval,
  currency,
  currencySwitchable,
  recommendedId,
}: {
  plans: PublicPlan[];
  /** Threaded to each card so a tier can name what it does not include. */
  addons: PublicPlan[];
  billingInterval: BillingInterval;
  currency: string;
  currencySwitchable: boolean;
  recommendedId: string | null;
}) {
  /**
   * One fold state for the whole row, not one per card.
   *
   * Per-card state looked reasonable and was wrong: the grid stretches every
   * card to the tallest, so expanding the 20-feature plan on its own left the
   * 5-feature plan as ~750px of empty card. Cards laid out side by side to be
   * compared have to stay in register — the moment one grows alone, the
   * comparison is gone and the row is mostly whitespace.
   */
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  /* Only worth a control if something is actually hidden. Counts the same rows
     the card builds — storage and the AI allowance became list rows rather than
     badges (T091826006), so counting `features` alone would under-report by up
     to two and hide the control on a card that is in fact folded. */
  const foldable = plans.some(
    (plan) =>
      plan.features.length +
        (plan.maxStorageGb > 0 ? 1 : 0) +
        (plan.includedTokensMonth > 0 ? 1 : 0) >
      FEATURES_BEFORE_FOLD
  );

  /**
   * Which card carries the filled button.
   *
   * A recommendation, where the catalog is big enough to earn one, still owns
   * it — that contract is untouched. The gap was the two-plan catalog we
   * actually ship: `recommendedId` is deliberately null below three plans, so
   * BOTH cards fell through to the ghost style and the page offered no primary
   * action anywhere. The dearest paid plan takes it instead, which states which
   * one is the product rather than recommending which to buy — the trial is the
   * way in, not a tier it competes with.
   */
  const ctaPlanId = useMemo(() => {
    if (recommendedId) return recommendedId;
    const paid = plans.filter((plan) => plan.priceMonthly > 0);
    if (paid.length === 0) return null;
    return paid.reduce((top, plan) => (plan.priceMonthly > top.priceMonthly ? plan : top)).id;
  }, [plans, recommendedId]);

  return (
    <>
      <div style={PLAN_GRID}>
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            billingInterval={billingInterval}
            currency={currency}
            currencySwitchable={currencySwitchable}
            recommended={plan.id === recommendedId}
            primaryCta={plan.id === ctaPlanId}
            featuresExpanded={featuresExpanded}
            addons={addons}
          />
        ))}
      </div>

      {foldable && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
          <button
            type="button"
            onClick={() => setFeaturesExpanded((expanded) => !expanded)}
            aria-expanded={featuresExpanded}
            /* Governs every list at once, so it names every list at once. */
            aria-controls={plans.map((plan) => planFeatureListId(plan.id)).join(" ")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "11px 20px",
              borderRadius: 999,
              border: HAIRLINE,
              background: "transparent",
              cursor: "pointer",
              fontFamily: MONO,
              fontSize: 12.5,
              letterSpacing: "0.02em",
              color: "var(--ink)",
            }}
          >
            {featuresExpanded ? "Show fewer features" : "Show all features"}
            {featuresExpanded ? (
              <ChevronUp size={14} aria-hidden="true" style={{ flex: "none" }} />
            ) : (
              <ChevronDown size={14} aria-hidden="true" style={{ flex: "none" }} />
            )}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * The add-on catalog, as compact cards.
 *
 * These used to render through PlanGrid — the same card, the same 42px price,
 * the same primary CTA as an actual plan, eighteen times over. One wrong
 * decision produced four wrong details: every add-on advertised "0 users" and
 * "0 GB" (it governs neither), every one offered "Get Started" (an add-on
 * attaches to a plan, so there is nothing to start), the five with no price
 * yet shouted "Custom" in the largest type on the page, and each carried a
 * full feature list. The section ran to half the page on desktop and fourteen
 * phone screens.
 *
 * Still cards, deliberately at a lower weight than a plan: four to a row
 * rather than three, a smaller radius, and the price as a line rather than a
 * headline. A plan is a choice and gets a card that argues; an add-on is a
 * lookup and gets a card that states.
 *
 * WHY THE LINK IS AN OVERLAY, NOT A WRAPPER
 * The whole tile navigates to /contact, and the feature list also expands in
 * place. An anchor wrapped round the card could not do both: a <button> inside
 * an <a> is interactive content nested in interactive content, which browsers
 * and screen readers both handle badly. So the anchor is an absolutely
 * positioned sibling covering the tile, and the expand button sits above it on
 * the z-axis — two separate targets that happen to overlap, which is what they
 * actually are. Text selection inside the tile is the price of the pattern.
 */
function AddonGrid({
  plans,
  billingInterval,
  currency,
}: {
  plans: PublicPlan[];
  billingInterval: BillingInterval;
  currency: string;
}) {
  /* One id rather than state per tile: only ever one card is hovered, and
     eighteen independent booleans would re-render the whole grid each time. */
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const toggle = (id: string) =>
    setExpandedIds((current) => ({ ...current, [id]: !current[id] }));

  return (
    <div
      style={{
        display: "grid",
        gap: 14,
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 268px), 1fr))",
        /* `stretch` so every tile in a row ends on the same line. `start` was
           tried, to stop an expanded tile stretching its neighbours, and the
           cure was worse: modules carry 1–12 features, so the grid sat visibly
           ragged in the state everyone actually sees.

           Expanding does now grow the other three in that row, but the two
           cases are not equal. The plan row's 757px void was permanent and hit
           a two-card row on load; this is a few rows of a quarter-width tile,
           in one row of four, only after a deliberate click. Alignment at rest
           beats alignment while poking at it. */
        alignItems: "stretch",
      }}
    >
      {plans.map((plan) => {
        const price = resolvePrice(plan, currency, billingInterval);
        /* Same helper the plan cards use, so an unpriced module reads "Custom"
           in both places and the two cannot drift.

           What was wrong before was never the word — it was the word set in
           42px as the loudest thing on a plan-weight card, on five cards at
           once. Here it is a 13px line in the card's foot, stating a fact at
           the weight a fact deserves. */
        const slots = priceSlots(price, billingInterval);
        const amount = slots.unit === null ? slots.figure : `${slots.figure} ${slots.unit}`;
        const seat =
          price.seatPrice > 0
            ? `+${formatMoney(price.seatPrice, price.currency)} per seat`
            : null;

        const hovered = hoveredId === plan.id;
        const expanded = expandedIds[plan.id] === true;
        const hiddenCount = plan.features.length - ADDON_CHECKS;
        const shownFeatures = expanded
          ? plan.features
          : plan.features.slice(0, ADDON_CHECKS);
        const listId = `addon-${plan.id}-features`;

        return (
          <div
            key={plan.id}
            onMouseEnter={() => setHoveredId(plan.id)}
            onMouseLeave={() =>
              setHoveredId((current) => (current === plan.id ? null : current))
            }
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              padding: "18px 18px 16px",
              borderRadius: 14,
              border: hovered ? "1px solid rgba(var(--ink-rgb),.34)" : HAIRLINE,
              background: "var(--paper2)",
              transition: "border-color .2s, transform .2s",
              transform: hovered ? "translateY(-2px)" : undefined,
            }}
          >
            {/* Covers the tile, carries the tile's accessible name, and sits
                UNDER the expand button so that button keeps its own click. */}
            <Link
              to="/contact"
              aria-label={`Talk to sales about ${plan.name}`}
              onFocus={() => setHoveredId(plan.id)}
              onBlur={() =>
                setHoveredId((current) => (current === plan.id ? null : current))
              }
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                borderRadius: 14,
              }}
            />

            <p
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.3,
                position: "relative",
                pointerEvents: "none",
              }}
            >
              {plan.name}
            </p>

            {plan.description && (
              /* Two lines, not three: a catalog of eighteen reads as a grid
                 only if every tile is the same shape, and the third line was
                 where the ragged edges came from. */
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: dimOn(false),
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  position: "relative",
                  pointerEvents: "none",
                }}
              >
                {plan.description}
              </p>
            )}

            {plan.features.length > 0 && (
              <ul
                id={listId}
                style={{
                  listStyle: "none",
                  margin: "12px 0 0",
                  padding: 0,
                  position: "relative",
                  pointerEvents: "none",
                }}
              >
                {shownFeatures.map((feature) => (
                  <li
                    key={feature}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "3px 0",
                    }}
                  >
                    {/* No rules between these, unlike the plan cards: at four
                        rows in a quarter-width tile, separators read as clutter
                        rather than as the structure they give a long list. */}
                    <Check
                      size={12}
                      aria-hidden="true"
                      style={{ flex: "none", marginTop: 4, color: "var(--acc2)" }}
                    />
                    <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>
                      {formatFeature(feature)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => toggle(plan.id)}
                aria-expanded={expanded}
                aria-controls={listId}
                /* Raised above the tile-wide link, and the only thing in here
                   that takes a pointer — everything else is inert so a click
                   anywhere else still reaches the link beneath. */
                style={{
                  position: "relative",
                  zIndex: 1,
                  alignSelf: "flex-start",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  margin: "7px 0 0",
                  marginLeft: 20,
                  padding: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: "0.02em",
                  color: "var(--acc2)",
                }}
              >
                <span style={SR_ONLY}>{plan.name}:</span>
                {expanded ? "show fewer" : `+${hiddenCount} more`}
                {expanded ? (
                  <ChevronUp size={11} aria-hidden="true" style={{ flex: "none" }} />
                ) : (
                  <ChevronDown size={11} aria-hidden="true" style={{ flex: "none" }} />
                )}
              </button>
            )}

            {/* Price and seat share ONE line, above a rule that gives the tile
                a foot. Stacking them meant a card with seat pricing stood a
                line taller than one without, so eighteen tiles never settled
                onto a baseline; now every card ends the same way. */}
            <p
              style={{
                margin: 0,
                /* `auto` is what makes the row's shared height do any work:
                   without it the foot follows the content and a three-check
                   module's price floats mid-card while its neighbour's sits at
                   the bottom. This pins every price to the same line. */
                marginTop: "auto",
                paddingTop: 13,
                borderTop: HAIRLINE,
                fontFamily: MONO,
                fontSize: 13,
                display: "flex",
                alignItems: "baseline",
                gap: 7,
                flexWrap: "wrap",
                position: "relative",
                pointerEvents: "none",
              }}
            >
              <span>{amount}</span>
              {seat && (
                <span style={{ fontSize: 11.5, color: dimOn(false) }}>{seat}</span>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The loading placeholder. Static rather than pulsing: the canvas ships one
 * keyframe that could stand in for a shimmer and it scales as well as fades,
 * which on a full-width bar reads as a glitch rather than as loading.
 */
function PlanCardSkeleton() {
  const bar = (width: string, height: number, marginTop: number): React.CSSProperties => ({
    width,
    height,
    marginTop,
    borderRadius: 6,
    background: "rgba(var(--ink-rgb),.07)",
  });

  return (
    <div
      aria-hidden="true"
      style={{
        height: "100%",
        background: "var(--paper2)",
        border: HAIRLINE,
        borderRadius: 18,
        padding: 28,
      }}
    >
      <div style={bar("34%", 22, 0)} />
      <div style={bar("68%", 12, 14)} />
      <div style={bar("50%", 38, 26)} />
      <div style={{ marginTop: 26 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} style={bar("100%", 12, i === 0 ? 0 : 12)} />
        ))}
      </div>
    </div>
  );
}

/** One row of the comparison: a label and one cell per plan. */
type CompareRow = {
  key: string;
  label: string;
  /** Rendered value per plan id, or `true`/`false` for an included/excluded feature. */
  cells: (string | boolean)[];
};

/** A row whose every plan says the same thing — it answers "what changes" with "nothing". */
function isSharedRow(row: CompareRow): boolean {
  return row.cells.length > 1 && new Set(row.cells.map(String)).size === 1;
}

/**
 * What actually changes between the plans.
 *
 * Built entirely from the catalog: the limit rows come from the numeric fields
 * every plan carries, the feature rows from the union of the feature ids the
 * plans ship, so a capability added to the catalog appears here without a code
 * change. Rows that no plan populates (seat pricing, AI tokens) are not
 * rendered — an all-empty row teaches nothing.
 *
 * The union is the right set to BUILD from and the wrong set to SHOW: on the
 * live catalog 12 of its 28 rows read "Included / Included", so a section
 * titled "what actually changes" spent 43% of itself on what does not. Rows
 * identical across every plan are therefore folded away by default and stay
 * one click from view — hiding what both plans include would be hiding good
 * news, but it should not be the thing a buyer scans first.
 */
function ComparisonTable({
  plans,
  billingInterval,
  currency,
  recommendedId,
}: {
  plans: PublicPlan[];
  billingInterval: BillingInterval;
  currency: string;
  recommendedId: string | null;
}) {
  const rows = useMemo<CompareRow[]>(() => {
    const prices = plans.map((plan) => resolvePrice(plan, currency, billingInterval));
    const out: CompareRow[] = [];

    out.push({
      key: "price",
      label: billingInterval === "yearly" ? "Annual price" : "Monthly price",
      // Same helper as the cards, flattened to one cell string. Sharing it is
      // the point: the table sits on the same page as the cards, so a trial
      // reading "Free for 14 days" up there and "Custom" down here would be a
      // visible contradiction.
      cells: prices.map((p) => {
        const { figure, unit } = priceSlots(p, billingInterval);
        return unit === null ? figure : `${figure} ${unit}`;
      }),
    });

    if (prices.some((p) => p.seatPrice > 0)) {
      out.push({
        key: "seat",
        label: "Per employee seat",
        cells: prices.map((p) =>
          p.seatPrice > 0
            ? `${formatMoney(p.seatPrice, p.currency)} ${priceSuffix(p.currency, billingInterval)}`
            : "—"
        ),
      });
    }

    out.push({
      key: "users",
      label: "Users",
      cells: plans.map((plan, i) => {
        // A seat-priced plan's user count IS the seat count the owner buys, so
        // the 999999 "Unlimited" sentinel would contradict the "Per employee
        // seat" row immediately above. The card drops its users badge for the
        // same reason; here the column has to stay filled, so it says what
        // actually governs.
        if ((prices[i]?.seatPrice ?? 0) > 0) return "Per seat";
        if (plan.maxUsers >= 999999) return "Unlimited";
        return plan.maxUsers.toLocaleString();
      }),
    });

    out.push({
      key: "storage",
      label: "Storage",
      cells: plans.map((plan) => `${plan.maxStorageGb} GB`),
    });

    if (plans.some((plan) => plan.includedTokensMonth > 0)) {
      out.push({
        key: "tokens",
        label: "AI tokens per month",
        cells: plans.map((plan) =>
          plan.includedTokensMonth > 0 ? formatTokens(plan.includedTokensMonth) : "—"
        ),
      });
    }

    /* Every feature any plan ships, in reading order of its display label, so
       the list is stable regardless of the order the catalog returns. */
    const featureIds = [...new Set(plans.flatMap((plan) => plan.features))];
    const featureRows = featureIds
      .map((id) => ({
        key: `feature-${id}`,
        label: formatFeature(id),
        cells: plans.map((plan) => plan.features.includes(id)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...out, ...featureRows];
  }, [plans, billingInterval, currency]);

  const isNarrow = useIsNarrow();
  const [showShared, setShowShared] = useState(false);

  const sharedCount = useMemo(() => rows.filter(isSharedRow).length, [rows]);
  const visibleRows = useMemo(
    () => (showShared ? rows : rows.filter((row) => !isSharedRow(row))),
    [rows, showShared]
  );

  const CELL: React.CSSProperties = {
    padding: isNarrow ? "11px 9px" : "14px 18px",
    fontSize: isNarrow ? 12 : 14,
    textAlign: "left",
    verticalAlign: "top",
  };

  /* Wide: the label column stays put while the plan columns scroll under it, so
     a row never loses the thing it is describing, and its background has to be
     opaque or the scrolled content shows through.

     Narrow: nothing scrolls any more, so nothing needs pinning — the sticky
     offset is dropped along with the wide label width. A table that fits is
     better than a table that scrolls well. */
  const STICKY_LABEL: React.CSSProperties = {
    ...CELL,
    ...(isNarrow
      ? { minWidth: 0, width: "38%" }
      : { position: "sticky", left: 0, zIndex: 1, minWidth: "11rem" }),
    fontWeight: 500,
    background: "var(--paper2)",
  };

  return (
    <>
      <div
        /* Only a scrolling box needs to be focusable and announced as a region:
           at narrow widths the table fits, so the keyboard affordance for a
           scroll that cannot happen is dropped with it. */
        {...(isNarrow
          ? {}
          : { tabIndex: 0, role: "region", "aria-label": "Plan comparison" })}
        style={{
          marginTop: 22,
          overflowX: isNarrow ? "visible" : "auto",
          borderRadius: 18,
          border: HAIRLINE,
          background: "var(--paper2)",
        }}
      >
        <table
          style={{
            width: "100%",
            /* The 40rem floor is what forced the sideways scroll on a phone —
               640px of table in a 400px column. Below the breakpoint the table
               is allowed to fit instead, paid for with tighter cells and a
               narrower label column rather than with a scrollbar. */
            ...(isNarrow ? { tableLayout: "fixed" } : { minWidth: "40rem" }),
            borderCollapse: "collapse",
            fontFamily: SANS,
          }}
        >
          <caption style={SR_ONLY}>
            Every plan compared by price, limits, and included capabilities.
          </caption>
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  ...STICKY_LABEL,
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: "-0.01em",
                  verticalAlign: "bottom",
                  borderBottom: RULE,
                }}
              >
                Plan
              </th>
              {plans.map((plan) => (
                <th
                  key={plan.id}
                  scope="col"
                  style={{ ...CELL, verticalAlign: "bottom", borderBottom: RULE }}
                >
                  <span
                    style={{
                      fontFamily: SANS,
                      fontWeight: 600,
                      fontSize: 15,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {plan.name}
                  </span>
                  {plan.id === recommendedId && (
                    <span
                      style={{
                        display: "block",
                        width: "fit-content",
                        marginTop: 8,
                        borderRadius: 999,
                        padding: "3px 10px",
                        background: "var(--acc2)",
                        color: "#101216",
                        fontFamily: MONO,
                        fontSize: 10,
                        letterSpacing: ".1em",
                        textTransform: "uppercase",
                        lineHeight: 1.4,
                      }}
                    >
                      Recommended
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              /* No `data-rv` on these rows: the table re-renders whenever the
                 billing toggle flips, and the reveal observer only runs at
                 mount. */
              <tr key={row.key}>
                <th
                  scope="row"
                  style={{
                    ...STICKY_LABEL,
                    color: "rgba(var(--ink-rgb),.66)",
                    borderTop: HAIRLINE,
                  }}
                >
                  {row.label}
                </th>
                {row.cells.map((cell, j) => (
                  <td
                    key={plans[j]?.id ?? j}
                    style={{
                      ...CELL,
                      fontFamily: typeof cell === "boolean" ? SANS : MONO,
                      fontSize: typeof cell === "boolean" ? 14 : 13,
                      color: "var(--ink)",
                      borderTop: HAIRLINE,
                    }}
                  >
                    {typeof cell === "boolean" ? (
                      cell ? (
                        <>
                          {/* Included and excluded are told apart by glyph and
                              by the words behind them, never by colour alone —
                              the accent on the tick is decoration on top of a
                              distinction that already holds without it. */}
                          <Check size={16} aria-hidden="true" style={{ color: "var(--acc2)" }} />
                          <span style={SR_ONLY}>Included</span>
                        </>
                      ) : (
                        <>
                          <Minus
                            size={16}
                            aria-hidden="true"
                            style={{ color: "rgba(var(--ink-rgb),.62)" }}
                          />
                          <span style={SR_ONLY}>Not included</span>
                        </>
                      )
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sharedCount > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
          <button
            type="button"
            onClick={() => setShowShared((shown) => !shown)}
            aria-expanded={showShared}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "11px 20px",
              borderRadius: 999,
              border: HAIRLINE,
              background: "transparent",
              cursor: "pointer",
              fontFamily: MONO,
              fontSize: 12.5,
              letterSpacing: "0.02em",
              color: "var(--ink)",
            }}
          >
            {/* Says what they are, not just how many: "12 more rows" reads as
                withheld detail, "included in every plan" reads as the reason
                they were not worth the buyer's first scan. */}
            {showShared
              ? "Hide what every plan includes"
              : `Show ${sharedCount} more included in every plan`}
            {showShared ? (
              <ChevronUp size={14} aria-hidden="true" style={{ flex: "none" }} />
            ) : (
              <ChevronDown size={14} aria-hidden="true" style={{ flex: "none" }} />
            )}
          </button>
        </div>
      )}
    </>
  );
}

type FaqItem = { q: string; a: string; link?: { to: string; label: string } };

/**
 * Billing questions, answered only where the catalog or the code can back the
 * answer up.
 *
 * Nothing here states a trial length, a discount percentage, a refund window, or
 * a cancellation rule, because none of those exist anywhere in this repository
 * to verify against. Where a buyer needs one of those, the answer says so and
 * routes to a person. The currency, seat-pricing, and add-on entries only render
 * when the loaded catalog actually raises the question.
 */
function PricingFaq({
  currencySwitchable,
  hasSeatPricing,
  hasAddons,
}: {
  currencySwitchable: boolean;
  hasSeatPricing: boolean;
  hasAddons: boolean;
}) {
  const [open, setOpen] = useState<number | null>(0);

  const faqs = useMemo<FaqItem[]>(() => {
    const items: FaqItem[] = [
      {
        q: "Are these the real prices?",
        a: "Yes. Every figure on this page is read from our live plan catalog when the page loads, the same records our Admin Console edits, rather than typed into the page by hand. A change to a plan reaches this page once the public cache refreshes.",
      },
      {
        q: "What is the difference between monthly and annual?",
        a: "They are two separately published prices, not a percentage we apply to one another. Switch the toggle to Annual to see each plan's real annual price; where that price comes in under twelve monthly payments, the card shows the exact difference.",
      },
    ];

    if (currencySwitchable) {
      items.push({
        q: "Why is one plan shown in a different currency?",
        a: "Because we only show prices we actually sell at. A plan with a published price point in the currency you picked is shown in that currency; a plan without one is shown in the currency it is really sold in, labelled with that currency code. We never convert a price ourselves. A converted figure would not be the figure you are charged. The currency you are billed in is confirmed at checkout.",
      });
    }

    if (hasSeatPricing) {
      items.push({
        q: "What does “per employee seat” mean?",
        a: "Those plans publish two amounts. The large figure is a flat base fee covering the account owner; the per-seat amount is billed for each employee seat you buy on top of it. Both are shown on the plan card, in the same currency.",
      });
    }

    items.push({
      q: "What happens when we reach a plan's limits?",
      a: "Each plan publishes its own limits: users, storage, and where it applies monthly AI tokens. Every one of them is listed on the plan card. What happens when you reach one depends on the plan and the contract, so we would rather confirm it for the plan you are actually considering than print a rule that turns out not to be yours.",
      link: { to: "/contact", label: "Ask about limits" },
    });

    if (hasAddons) {
      items.push({
        q: "Do add-ons replace a plan?",
        a: "No. Add-ons are priced separately and extend a base plan with extra capability or storage. They are listed with their own published prices below the plans.",
      });
    }

    items.push({
      q: "Is there a trial, and how do we cancel?",
      a: "We do not publish trial terms or a cancellation policy on this page, because we will not print terms we have not confirmed for your contract. Ask us and we will send the current terms in writing before you commit to anything.",
      link: { to: "/contact", label: "Get the terms in writing" },
    });

    return items;
  }, [currencySwitchable, hasSeatPricing, hasAddons]);

  return (
    <div style={{ borderTop: DARK_RULE }}>
      {faqs.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q} style={{ borderBottom: DARK_RULE }}>
            <h3 style={{ margin: 0 }}>
              <button
                type="button"
                id={`pricing-faq-trigger-${i}`}
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-controls={`pricing-faq-panel-${i}`}
                className="dc-h-cyan"
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "21px 4px",
                  fontFamily: SANS,
                  fontSize: 16.5,
                  fontWeight: 600,
                  color: DARK_INK,
                  textAlign: "left",
                  transition: "color .2s",
                }}
              >
                {item.q}
                <span
                  aria-hidden="true"
                  style={{
                    flex: "none",
                    fontFamily: MONO,
                    fontSize: 18,
                    fontWeight: 400,
                    color: DARK_ACCENT,
                  }}
                >
                  {isOpen ? "−" : "+"}
                </span>
              </button>
            </h3>

            {/* Unmounted when closed rather than collapsed in place: a link
                inside a zero-height container is still in the tab order. */}
            {isOpen && (
              <div
                id={`pricing-faq-panel-${i}`}
                role="region"
                aria-labelledby={`pricing-faq-trigger-${i}`}
                style={{ padding: "0 40px 24px 4px" }}
              >
                <p
                  style={{
                    margin: 0,
                    maxWidth: "68ch",
                    fontSize: 15,
                    lineHeight: 1.7,
                    color: DARK_DIM,
                  }}
                >
                  {item.a}
                </p>
                {item.link && (
                  <Link
                    to={item.link.to}
                    className="dc-h-cyan-soft"
                    style={{
                      display: "inline-block",
                      marginTop: 14,
                      fontSize: 14.5,
                      fontWeight: 600,
                      color: DARK_ACCENT,
                      transition: "color .2s",
                    }}
                  >
                    {item.link.label}
                  </Link>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Pricing() {
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  // Display currency is AUTOMATIC and non-selectable: the backend resolves it
  // from the request's network location (Canada → CAD, everyone else → USD,
  // constrained to a currency the catalog sells) via /public/pricing/geo. No
  // device guessing, no switcher, no persisted choice — a geo-endpoint failure
  // simply leaves the base catalog currency.
  const [displayCurrency, setDisplayCurrency] = useState<string>(BASE_DISPLAY_CURRENCY);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await pricingApi.getAll();
      setPlans(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPlans();
  }, [fetchPlans]);

  /* Cheapest first, by the base-currency monthly price. Sorting on the base
     price rather than the displayed one keeps the columns from reshuffling when
     the visitor flips currency or interval — the order is a property of the
     catalog, not of the toggles. */
  /**
   * The trial is no longer a tier (T091826006). It was a card in the plan row,
   * which put a thing you cannot buy beside the things you can and made the row
   * read as a choice between them.
   *
   * It is still SERVED — the catalog keeps it, and its `description` is the
   * sentence rendered under the billing toggle. Hiding it at the endpoint
   * instead would have meant hardcoding "14 days" and "25 employees" into the
   * bundle, and the whole reason that copy lives on the plan is that the trial
   * length is editable in Admin Console without a deploy.
   */
  const trialPlan = useMemo(
    () => plans.find((p) => p.planType === "base" && p.isTrial === true) ?? null,
    [plans]
  );

  /* Cheapest first, by the base-currency monthly price. Sorting on the base
     price rather than the displayed one keeps the columns from reshuffling when
     the visitor flips currency or interval — the order is a property of the
     catalog, not of the toggles.

     The unpriced Enterprise tier sorts first on a raw price sort, because 0 is
     the smallest number — so unpriced plans are forced to the end instead. A
     "talk to us" tier is the ceiling of the range, not the floor of it. */
  const basePlans = useMemo(
    () =>
      plans
        .filter((p) => p.planType === "base" && p.isTrial !== true)
        .sort((a, b) => {
          const aUnpriced = a.priceMonthly <= 0;
          const bUnpriced = b.priceMonthly <= 0;
          if (aUnpriced !== bUnpriced) return aUnpriced ? 1 : -1;
          return a.priceMonthly - b.priceMonthly;
        }),
    [plans]
  );
  const addons = useMemo(() => plans.filter((p) => p.planType === "addon"), [plans]);

  /* The display currency is resolved from the visitor's location, not chosen.
     prod dropped the manual switcher (1d3e24e) in favour of this, so the
     catalog-union/localStorage machinery the redesign carried is gone with it.
     `currencySwitchable` stays as a constant so the components that thread it
     keep their shape — and it is false, which is what "not selectable" means. */
  useEffect(() => {
    let cancelled = false;
    void pricingApi
      .getGeoContext()
      .then((geo) => {
        if (!cancelled && geo.currency) setDisplayCurrency(geo.currency);
      })
      .catch(() => {
        // No geo signal (endpoint absent/unreachable) — keep the base currency.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currencySwitchable = false;

  /**
   * The recommended tier is the middle base plan by price — a stated default,
   * not a popularity claim, because no adoption data exists to make one from.
   * It is identified by id so it survives re-sorting, and it is withheld below
   * three plans: with one or two, a recommendation adds nothing a buyer cannot
   * see for themselves.
   */
  const recommendedId = useMemo(() => {
    if (basePlans.length < 3) return null;
    return basePlans[Math.floor(basePlans.length / 2)]?.id ?? null;
  }, [basePlans]);

  const hasSeatPricing = useMemo(
    () =>
      plans.some(
        (plan) =>
          resolvePrice(plan, displayCurrency, "monthly").seatPrice > 0 ||
          resolvePrice(plan, displayCurrency, "yearly").seatPrice > 0
      ),
    [plans, displayCurrency]
  );

  const hasPlans = !loading && !error && plans.length > 0;

  return (
    <DcPage>
      {/* ═══ HERO ═══ */}
      <section
        className="site-gutter"
        style={{ ...SECTION, paddingTop: 104, paddingBottom: 84 }}
        aria-labelledby="pricing-heading"
      >
        <div style={INNER}>
          <div data-rv="" style={{ maxWidth: 880 }}>
            <Mark />
            <h1
              id="pricing-heading"
              style={{
                margin: 0,
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "clamp(38px, 4.6vw, 64px)",
                lineHeight: 1.08,
                letterSpacing: "-0.025em",
              }}
            >
              Plans that scale with you.
            </h1>
          </div>

          <p
            data-rv=""
            style={{
              margin: "24px 0 0",
              maxWidth: "58ch",
              fontSize: 17,
              lineHeight: 1.7,
              color: "rgba(var(--ink-rgb),.7)",
            }}
          >
            Start with the essentials and add capabilities as you grow. Every plan
            runs on the same unified platform.
          </p>

          {/* The reveal sits on this wrapper, not on the control: the wrapper
              is mounted once and never replaced, so flipping the interval
              cannot strand a re-rendered node at opacity 0. */}
          <div data-rv="" style={{ marginTop: 34 }}>
            <SegmentedControl<BillingInterval>
              label="Billing interval"
              value={billingInterval}
              onChange={setBillingInterval}
              segments={[
                { value: "monthly", label: "Monthly" },
                { value: "yearly", label: "Annual" },
              ]}
            />

            {/* The trial, demoted from a card to a line (T091826006). It sits
                under the interval control rather than above the grid because it
                is not a third option to weigh against the tiers — it is the
                answer to "can I look first", which is a question you ask before
                you start comparing prices.

                Rendered only when the catalog actually serves a trial, so
                retiring the trial removes this line with it and cannot strand a
                promise the product no longer keeps. */}
            {trialPlan?.description && (
              <p
                style={{
                  margin: "16px 0 0",
                  maxWidth: "52ch",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: "rgba(var(--ink-rgb),.62)",
                }}
              >
                {trialPlan.description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ═══ PLANS ═══ */}
      <section className="site-gutter" style={{ ...SECTION, paddingTop: 0, paddingBottom: 112 }} aria-label="Plans">
        <div style={INNER}>
          {loading ? (
            <div style={PLAN_GRID}>
              {Array.from({ length: 3 }).map((_, i) => (
                <PlanCardSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <div style={{ maxWidth: 520, margin: "0 auto", padding: "48px 0", textAlign: "center" }}>
              <div
                aria-hidden="true"
                style={{
                  display: "flex",
                  width: 48,
                  height: 48,
                  margin: "0 auto 22px",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 14,
                  border: HAIRLINE,
                  background: "var(--paper2)",
                  color: "var(--acc2)",
                }}
              >
                <AlertCircle size={22} />
              </div>
              <h2 style={{ ...H2, fontSize: "clamp(24px, 2.6vw, 34px)" }}>
                Pricing is momentarily unavailable
              </h2>
              <p
                style={{
                  ...BODY,
                  margin: "14px auto 28px",
                  maxWidth: "48ch",
                  fontSize: 15,
                  lineHeight: 1.7,
                }}
              >
                We couldn't load current plans. Try again, or reach out and we'll walk you
                through pricing directly.
              </p>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                }}
              >
                <button
                  type="button"
                  onClick={() => void fetchPlans()}
                  className="dc-h-btn-lg"
                  style={BTN_PRIMARY}
                >
                  <RefreshCw size={16} aria-hidden="true" />
                  Retry
                </button>
                <Link to="/contact" className="dc-h-ghost" style={BTN_GHOST}>
                  Contact us
                  <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </div>
          ) : /* `basePlans`, not `plans` — this section renders TIERS, so it has
                 to ask whether there are tiers. Keyed on `plans.length` it could
                 not fire while the catalog still served add-ons, and the add-ons
                 alone keep that non-zero: the heading and the billing toggle
                 would render over an empty grid with no explanation, while the
                 module section carried on below as if nothing were missing.

                 Carried from PR #69 on this repo, which identified it against
                 the trial being withdrawn; this work supersedes that branch. It
                 matters more now than it did there — the tier list became
                 genuinely dynamic when the trial stopped being one of them. */
          basePlans.length === 0 ? (
            <div style={{ maxWidth: 520, margin: "0 auto", padding: "48px 0", textAlign: "center" }}>
              <h2 style={{ ...H2, fontSize: "clamp(24px, 2.6vw, 34px)" }}>
                Pricing coming soon
              </h2>
              <p
                style={{
                  ...BODY,
                  margin: "14px auto 28px",
                  maxWidth: "48ch",
                  fontSize: 15,
                  lineHeight: 1.7,
                }}
              >
                {addons.length > 0
                  ? "We're between plans right now. The modules below are still available — tell us what you need and we'll put a package together."
                  : "Plans aren't published yet. Get in touch and we'll tailor a package to your team."}
              </p>
              <Link to="/contact" className="dc-h-btn-lg" style={BTN_PRIMARY}>
                Talk to us
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <>
              {recommendedId && (
                <p
                  style={{
                    ...BODY,
                    margin: "0 auto 34px",
                    maxWidth: "68ch",
                    textAlign: "center",
                    fontSize: 14.5,
                    lineHeight: 1.7,
                  }}
                >
                  “Recommended” is our own suggestion, the middle tier, not a
                  popularity figure. The comparison below shows exactly what changes
                  between plans.
                </p>
              )}
              {/* No `basePlans.length > 0` guard: the branch above now owns the
                  empty case, so this one is unreachable with an empty list. */}
              <PlanGrid
                plans={basePlans}
                addons={addons}
                billingInterval={billingInterval}
                currency={displayCurrency}
                currencySwitchable={currencySwitchable}
                recommendedId={recommendedId}
              />
            </>
          )}
        </div>
      </section>

      {/* ═══ COMPARISON ═══ */}
      {hasPlans && basePlans.length > 1 && (
        <section className="site-gutter" style={SECTION} aria-labelledby="compare-heading">
          <div style={INNER}>
            <div style={{ maxWidth: 760 }}>
              <Mark />
              <h2 id="compare-heading" style={H2}>
                What actually changes between plans.
              </h2>
            </div>
            <ComparisonTable
              plans={basePlans}
              billingInterval={billingInterval}
              currency={displayCurrency}
              recommendedId={recommendedId}
            />
          </div>
        </section>
      )}

      {/* ═══ ADD-ONS ═══ */}
      {hasPlans && addons.length > 0 && (
        <section className="site-gutter" style={SECTION} aria-labelledby="addons-heading">
          <div style={INNER}>
            <div style={{ maxWidth: 760, marginBottom: 36 }}>
              <Mark />
              {/* A step below the other section headings on purpose. At the
                  shared H2 size this catalog announced itself as loudly as the
                  plan comparison, which left the page reading as four equal
                  peers with no route through it. It is the last thing a buyer
                  needs, so it is the quietest heading. */}
              <h2
                id="addons-heading"
                style={{ ...H2, fontSize: "clamp(26px, 2.4vw, 34px)", marginBottom: 14 }}
              >
                Add-ons.
              </h2>
              <p style={{ ...BODY, maxWidth: "62ch", lineHeight: 1.7 }}>
                Priced separately, and added to a plan rather than replacing one.
              </p>
            </div>
            <AddonGrid
              plans={addons}
              billingInterval={billingInterval}
              currency={displayCurrency}
            />
          </div>
        </section>
      )}

      {/* ═══ BILLING QUESTIONS ═══
          On the canvas's dark band, so the page lands on the same ground its
          footer stands on rather than stopping short of it. */}
      {hasPlans && (
        <section
          style={{
            position: "relative",
            overflow: "clip",
            background: DARK_GROUND,
            color: DARK_INK,
          }}
          aria-labelledby="pricing-faq-heading"
        >
          {/* Not parallaxed: it is anchored to the section's midpoint. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "50%",
              right: -220,
              width: 640,
              height: 640,
              marginTop: -320,
              border: "1px solid rgba(56,229,255,.18)",
              borderRadius: "50%",
              pointerEvents: "none",
            }}
          />

          <div
            className="site-gutter"
            style={{
              maxWidth: 1240,
              margin: "0 auto",
              paddingTop: 118,
              paddingBottom: 110,
              position: "relative",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
              gap: "48px 72px",
              alignItems: "start",
            }}
          >
            <div style={{ position: "sticky", top: 100 }}>
              <Mark dark />
              <h2
                id="pricing-faq-heading"
                style={{ ...H2, fontSize: "clamp(28px, 3.2vw, 42px)" }}
              >
                Before you commit.
              </h2>
              <p
                style={{
                  margin: "20px 0 0",
                  maxWidth: "46ch",
                  fontSize: 16,
                  lineHeight: 1.7,
                  color: DARK_DIM,
                }}
              >
                What we can answer from the catalog is answered here. What depends
                on your contract, we will put in writing rather than guess at.
              </p>
              <Link
                to="/contact"
                className="dc-h-ghost-light"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 26,
                  border: "1px solid rgba(242,244,246,.3)",
                  color: DARK_INK,
                  padding: "15px 28px",
                  borderRadius: 999,
                  fontWeight: 600,
                  fontSize: 15,
                  transition: "border-color .2s, background .2s",
                }}
              >
                Talk to us about pricing
              </Link>
            </div>

            <PricingFaq
              currencySwitchable={currencySwitchable}
              hasSeatPricing={hasSeatPricing}
              hasAddons={addons.length > 0}
            />
          </div>
        </section>
      )}
    </DcPage>
  );
}
